/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'

import type { EuiButtonSize, EuiButtonColor } from '@elastic/eui'

import type { AnyClusterPlanInfo, SliderInstanceType, StackDeployment } from '@modules/ui-types'

import {
  getPlanVersion,
  getPlanInfo,
  getFirstSliderClusterFromGet,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { getDeploymentPlanAttemptId } from '@/lib/stackDeployments/selectors/configurationChangeAttempts'
import { sanitizeUpdateRequestBeforeSend } from '@/lib/stackDeployments/updates'
import { getSliderPrettyName } from '@/lib/sliders/messages'

import StackDeploymentUpdateDryRunWarnings from '../../StackDeploymentUpdateDryRunWarnings'
import StackDeploymentUpdateDryRunWarningCheck from '../../StackDeploymentUpdateDryRunWarningCheck'
import QuickDeploymentUpdateButton from '../QuickDeploymentUpdateButton'

import transformBeforeSave from './lib/transformBeforeSave'
import RetryStackDeploymentAttemptModal from './RetryStackDeploymentAttemptModal'

import type { QuickDeploymentUpdateCustomModalProps } from '../QuickDeploymentUpdateButton'
import type { DangerButtonModalProps } from '../../DangerButton'
import type { FunctionComponent, ReactNode } from 'react'

export type Props = {
  deployment: StackDeployment
  sliderInstanceType: SliderInstanceType
  planAttempt?: AnyClusterPlanInfo
  isEmpty?: boolean
  disabled?: boolean
  hideAdminReapplyButton?: boolean
  size?: EuiButtonSize
  color?: EuiButtonColor
  children?: ReactNode
}

const RetryStackDeploymentAttemptButton: FunctionComponent<Props> = ({
  deployment,
  sliderInstanceType,
  planAttempt,
  disabled,
  isEmpty,
  size,
  color,
  hideAdminReapplyButton,
  children,
}) => {
  const resource = getFirstSliderClusterFromGet({ deployment, sliderInstanceType })!
  const planAttemptUnderRetry =
    planAttempt || getPlanInfo({ resource, state: `last_history_attempt` })

  if (
    hideAdminReapplyButton &&
    planAttemptUnderRetry &&
    planAttemptUnderRetry.source &&
    planAttemptUnderRetry.source.admin_id !== undefined
  ) {
    return null // only render buttons for user-originated plans in user console
  }

  const version = getPlanVersion({ plan: planAttemptUnderRetry?.plan })

  const defaultButtonText = (
    <FormattedMessage
      id='retry-slider-plan-button.text'
      defaultMessage='Retry {sliderName} changes'
      values={{
        sliderName: <FormattedMessage {...getSliderPrettyName({ sliderInstanceType, version })} />,
      }}
    />
  )

  const deploymentId = deployment.id
  const buttonText: ReactNode = children || defaultButtonText

  return (
    <StackDeploymentUpdateDryRunWarningCheck deploymentId={deploymentId}>
      {({ dryRunCheckPassed }) => (
        <QuickDeploymentUpdateButton
          data-test-id='reapply-plan-button'
          size={size}
          color={color}
          isEmpty={isEmpty}
          disabled={disabled || !planAttemptUnderRetry}
          planAttemptUnderRetry={planAttemptUnderRetry!}
          sliderInstanceType={sliderInstanceType}
          deployment={deployment}
          customizeModal={(props) => customizeModal(props, { dryRunCheckPassed })}
          pruneOrphans={false}
          transformBeforeSave={(deployment) => transformBeforeSave(deployment, sliderInstanceType)}
        >
          {buttonText}
        </QuickDeploymentUpdateButton>
      )}
    </StackDeploymentUpdateDryRunWarningCheck>
  )

  function customizeModal(
    props: QuickDeploymentUpdateCustomModalProps,
    { dryRunCheckPassed }: { dryRunCheckPassed: boolean },
  ): DangerButtonModalProps | undefined {
    if (!planAttemptUnderRetry) {
      return
    }

    const { updatePayload } = props
    const sanitizedUpdatePayload = sanitizeUpdateRequestBeforeSend({ deployment: updatePayload })

    return {
      title: (
        <FormattedMessage
          id='reapply-plan-button.title'
          defaultMessage='Confirm to reapply configuration {attempt}?'
          values={{
            attempt: `#${sliderInstanceType}-${getDeploymentPlanAttemptId({
              deployment,
              sliderInstanceType,
              planAttempt: planAttemptUnderRetry,
            })}`,
          }}
        />
      ),
      body: (
        <div>
          <StackDeploymentUpdateDryRunWarnings
            deploymentId={deploymentId}
            deployment={sanitizedUpdatePayload}
            spacerAfter={true}
          />

          <RetryStackDeploymentAttemptModal {...props} />
        </div>
      ),
      confirmButtonDisabled: !dryRunCheckPassed,
    }
  }
}

export default RetryStackDeploymentAttemptButton
